import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { map, tap } from 'rxjs/operators';
import { EmployeeRequestResponsibleForAnswerStatusEnum } from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { AddNewClockRegisterReviewRequestDialogData } from 'src/app/models/clock-register-review-request.model';
import { Location } from 'src/app/models/location.model';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'app-add-new-clock-register-review-request-dialog',
  templateUrl: './add-new-clock-register-review-request-dialog.component.html',
  styleUrls: ['./add-new-clock-register-review-request-dialog.component.css'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    MatError,
  ],
})
export class AddNewClockRegisterReviewRequestDialogComponent {
  dialog_ref = inject<MatDialogRef<AddNewClockRegisterReviewRequestDialogComponent>>(MatDialogRef);
  data = inject<AddNewClockRegisterReviewRequestDialogData>(MAT_DIALOG_DATA);
  protected form_builder = inject(UntypedFormBuilder);
  private api_service = inject(ApiService);
  private destroy_ref = inject(DestroyRef);

  readonly justification_min_length = 20;
  readonly saving = signal(false);
  readonly submitted = signal(false);
  form = this.buildForm();
  readonly locations = toSignal(
    this.api_service.getLocations({ user_id: this.data.user_id }).pipe(
      map(response => response.data ?? []),
      tap(locations => this.applyDefaultProposedLocation(locations))
    ),
    { initialValue: [] as Location[] }
  );

  save(): void {
    if (this.saving()) {
      return;
    }

    this.submitted.set(true);
    this.form.markAllAsTouched();
    this.syncNoChangeProposedErrorOnLocation();

    if (this.form.invalid) {
      return;
    }

    this.saving.set(true);

    const { clock_register_id, proposed_date, justification, status, proposed_location_id } = this.form.value;
    this.dialog_ref.close({
      clock_register_id,
      proposed_date: new Date(proposed_date),
      justification,
      status,
      proposed_location_id,
    });
  }

  private buildForm(): UntypedFormGroup {
    const date_pipe = new DatePipe('es-ES');

    const clock_register_created_at = this.data.clock_register_created_at
      ? date_pipe.transform(this.data.clock_register_created_at, 'yyyy-MM-ddTHH:mm')
      : '';

    const form = this.form_builder.group(
      {
        clock_register_id: [this.data.clock_register_id, Validators.required],
        proposed_date: [
          clock_register_created_at,
          Validators.compose([
            Validators.required,
            control => {
              const proposed_date_is_in_the_future = control.value && new Date(control.value) > new Date();
              if (proposed_date_is_in_the_future) {
                return { futureDate: true };
              }
              return null;
            },
          ]),
        ],
        justification: ['', Validators.compose([Validators.required, Validators.minLength(this.justification_min_length)])],
        status: [EmployeeRequestResponsibleForAnswerStatusEnum.PENDING, Validators.required],
        proposed_location_id: [null as number | null],
      },
      { validators: [this.proposedDateOrLocationIsDifferentFromCurrentValidator()] }
    );

    form.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(() => {
      if (this.submitted()) {
        this.syncNoChangeProposedErrorOnLocation();
      }
    });

    return form;
  }

  private proposedDateOrLocationIsDifferentFromCurrentValidator(): ValidatorFn {
    const date_pipe = new DatePipe('es-ES');
    const current_date = this.data.clock_register_created_at
      ? date_pipe.transform(this.data.clock_register_created_at, 'yyyy-MM-ddTHH:mm')
      : '';
    const current_location_id = this.data.current_location_id;

    return (control: AbstractControl): ValidationErrors | null => {
      const proposed_date = control.get('proposed_date')?.value;
      const proposed_location_id = control.get('proposed_location_id')?.value;

      if (!proposed_date) {
        return null;
      }

      const date_is_unchanged = proposed_date === current_date;
      const location_is_unchanged =
        proposed_location_id == null || (current_location_id != null && Number(current_location_id) === Number(proposed_location_id));

      if (date_is_unchanged && location_is_unchanged) {
        return { noChangeProposed: true };
      }

      return null;
    };
  }

  private syncNoChangeProposedErrorOnLocation(): void {
    const location_control = this.form.get('proposed_location_id');
    if (location_control == null) {
      return;
    }

    const errors = { ...(location_control.errors ?? {}) };

    if (this.form.hasError('noChangeProposed')) {
      errors['noChangeProposed'] = true;
      location_control.setErrors(errors);
      return;
    }

    if ('noChangeProposed' in errors) {
      delete errors['noChangeProposed'];
      location_control.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }
  }

  private applyDefaultProposedLocation(locations: Location[]): void {
    const current_location_id = this.data.current_location_id;
    if (current_location_id == null) {
      return;
    }

    const current_location_is_available = locations.some(location => location.id === current_location_id);
    if (current_location_is_available) {
      this.form.patchValue({ proposed_location_id: current_location_id });
    }
  }
}
